import { NETWORKS, fmtUsd } from '@platform/shared';
import type { HelpText } from '../../components/HelpTip';

const DAYS = ['Sundays', 'Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays'];

/** Explanations behind the "?" icons on the Wallet page, written for clients. */
export const HELP = {
  balance: {
    title: 'Balance',
    text: 'Your real money with us: everything you deposited, plus profits and minus losses from closed trades, fees and swap. It does not include trades that are still open.',
    example: 'Example: deposit $1,000 and close a trade with +$50 profit → balance $1,050.',
  },
  available: { title: 'Available', text: 'Money that is free right now. You can use it to open new trades or withdraw it.' },
  marginInUse: {
    title: 'Margin in use', learn: '/academy/leverage-margin/margin-used',
    text: 'Money set aside as a deposit to keep your open trades running, plus money held for orders waiting to be filled at your chosen price. When you close a trade, its margin returns to Available, plus or minus that trade’s profit or loss; cancelling an order releases its hold.',
    example: 'Example: a $1,000 trade at 10× leverage holds $100 as margin.',
  },
  managed: {
    title: 'Managed balance',
    text: 'Money on your managed account, traded for you by the master trader you copy. It is kept separately from your cash balance and moves with that account’s trades.',
  },
  equity: {
    title: 'Equity', learn: '/academy/leverage-margin/cross-margin',
    text: 'What your account is worth right now if you closed all your trades: your balance plus the profit or loss on open trades. It moves with the market.',
    example: 'Example: balance $1,050 and open trades at −$30 → equity $1,020.',
  },
  unrealized: {
    title: 'Unrealized P&L', learn: '/academy/first-trade/floating-pnl',
    text: 'Profit or loss on trades that are still open. It only becomes real, and is added to your balance, when you close the trade.',
  },
  withdrawable: {
    title: 'Withdrawable now',
    text: 'How much you can withdraw today. Money used as margin or reserved for orders cannot be withdrawn until those trades or orders are closed.',
  },
  credit: {
    title: 'Credit',
    text: 'Bonus funds given to you by Traders Portal. You can use credit to trade, and it counts toward your equity, but it cannot be withdrawn. Profit you make while trading with credit is yours and can be withdrawn.',
    example:
      'Trading losses never reduce your credit. Example: you deposit $1,000 and get $500 credit, so you trade with $1,500. If you lose $1,100, your balance shows −$100 and your credit stays $500. Your next deposit or profit covers the −$100 first.',
  },
  demo: {
    title: 'Demo account',
    text: 'A practice account with virtual money. Nothing you win or lose here is real, and it cannot be withdrawn or moved to your live account.',
  },
  sinceReset: { title: 'Since last reset', text: 'How much your demo account has gained or lost since it was last reset.' },
  resetDemo: (start: string | null): HelpText => ({
    title: 'Reset demo',
    text: `Sets your demo balance back to ${start ? fmtUsd(start, 0) : 'its starting amount'}. Close all demo trades first. Your live account is not affected.`,
  }),
  inProgress: { title: 'In progress', text: 'Deposits and withdrawals that have not finished yet. This list updates by itself — you do not need to refresh.' },
  confirmations: {
    title: 'Confirmations',
    text: `A crypto deposit is added to your balance after the blockchain confirms it a set number of times: ${NETWORKS.map((n) => `${n.confirmations} on ${n.id}`).join(', ')}. This usually takes a few minutes.`,
  },
  awaitingReview: {
    title: 'Awaiting review',
    text: 'We check every withdrawal before sending it, usually within 24 hours. The amount has already been taken from your balance. If you cancel it, or we reject it, the full amount comes straight back.',
  },
  ticketPending: {
    title: 'Pending',
    text: 'Our team has opened this for you and is checking it. Nothing has moved on your balance yet.',
  },
  ticketProcessing: {
    title: 'Processing',
    text: 'Approved. We are completing the transfer. A deposit is added to your balance when it completes; for a withdrawal the amount has already been taken from your balance.',
  },
  ticketDeclined: {
    title: 'Declined',
    text: 'Our team stopped this before any money moved, so your balance is unchanged. Contact support if you have questions.',
  },
  receive: {
    title: 'You receive / fee',
    text: 'Any withdrawal fee is taken from the amount you withdraw, so you receive the amount minus the fee. The fee for your coin or payout currency is shown before you confirm.',
    example: 'Example: withdraw $300 in USDT with no fee → you receive 300 USDT.',
  },
  // `when`: the period, as "in the last 30 days" or "from 3 Sep to 17 Oct".
  deposited: (when: string): HelpText => ({ title: 'Deposited', text: `Total deposits added to your live account ${when}.` }),
  withdrawn: (when: string): HelpText => ({
    title: 'Withdrawn',
    text: `Total withdrawals ${when}, including ones still awaiting review. Cancelled or rejected withdrawals are not counted.`,
  }),
  tradingPnl: (when: string): HelpText => ({ title: 'Trading PnL', text: `Profit or loss from trades you closed ${when}, before fees and swap.` }),
  fees: (swapHourUtc?: number, swapTripleDay?: number): HelpText => ({
    title: 'Fees & swap', learn: '/academy/risk-management/overnight-swap',
    text:
      'Fees are charged when you open a trade, and a commission also when you close it. Swap is a small overnight charge for keeping a trade open past ' +
      (swapHourUtc != null ? `${String(swapHourUtc).padStart(2, '0')}:00 UTC` : 'the daily cut-off') +
      (swapTripleDay != null && swapTripleDay >= 0 && swapTripleDay < 7 ? `; on ${DAYS[swapTripleDay]} it is charged ×3 to cover the weekend.` : '.'),
  }),
  moneyHistory: {
    title: 'Money transactions',
    text: 'Money you sent to your live account or took out of it, newest first, with where each one stands. A withdrawal is taken from your balance when you request it; if it is cancelled or rejected, the amount comes back as a separate “Withdrawal returned” line.',
  },
  transactions: {
    title: 'Trading activity',
    text: 'Everything else that changed your balance: closed trades, fees, swap, yield and credit, newest first. “Balance after” shows your balance right after each one, and “Day net” the day’s total without credit.',
  },
  accountId: { title: 'Account ID', text: 'Your account number. Quote it when you contact support so we can find your account quickly.' },
} satisfies Record<string, HelpText | ((...a: never[]) => HelpText)>;
